// @ts-nocheck
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";
import { css } from "styled-components";

/* Valtech Custom Code */
import Accordion, { AccordionPresentationProps } from "@insite/mobius/Accordion";
import { AccordionSectionPresentationProps } from "@insite/mobius/AccordionSection";
import ManagedAccordionSection from "@insite/mobius/AccordionSection/ManagedAccordionSection";
import Hidden, { HiddenProps } from "@insite/mobius/Hidden";
import { StyledClickable } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import { linkAltStyles, linkStyles } from "@store/Themes/common/mixins/link";
import { accordionFooterSectionProps } from "@store/Themes/common/props/accordionFooter";
import breakpoint from "@toolkit/Framework/Common/Utilities/breakpoint";
import breakpointValues from "@toolkit/Framework/Common/Utilities/breakpointValues";
import { pxToRem, pxToRemArray } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { gridGapStyles, siteWidthStyles, spacingStyles } from "@toolkit/Themes/common/mixins";

/* Store Custom Code - Start */
import { StyledImgWrapper } from "@store/MobiusOverrides/Img/Img";
import getEncodedColor from "@toolkit/Framework/Common/Utilities/getEncodedColor";

const separatorStyles = () => css`
    &::before {
        content: "";
        position: relative;
        width: 100%;
        height: ${pxToRem(1)};
        background-color: ${getColor("common.border")};
    }
`;
/* Store Custom Code - End */

const enum fields {
    contentTitle = "contentTitle",
    firstColumnTitle = "firstColumnTitle",
    secondColumnTitle = "secondColumnTitle",
    thirdColumnTitle = "thirdColumnTitle",
}

type Alignement = "left" | "center" | "right";

const baseLegalAlignStyles = (alignment: Alignement) => css`
    text-align: ${alignment};

    ul {
        text-align: ${alignment};
    }
`;

const legalAlignStyles = (alignment: Alignement) => css`
    ${baseLegalAlignStyles("left") /* Store Custom Code */}

    ${breakpoint("md")} {
        ${baseLegalAlignStyles(alignment)}
    }
`;

const footerLinkStyles = () => linkStyles("inherit", "neutral.contrast", false, true); // Store Custom Code
/* Valtech Custom Code */

interface OwnProps extends WidgetProps {
    extendedStyles?: FooterContainerStyles;
}

export interface FooterContainerStyles {
    widgetGridContainer?: GridContainerProps;
    logoGridItem?: GridItemProps;
    linkList1GridItem?: GridItemProps;
    linkList2GridItem?: GridItemProps;
    linkList3GridItem?: GridItemProps;
    linkList4GridItem?: GridItemProps;
    subscribeGridItem?: GridItemProps;
    socialLinksGridItem?: GridItemProps;
    siteMapLinksGridItem?: GridItemProps;
    richContentGridItem?: GridItemProps;
    logoWrapper?: InjectableCss;
    widgetWrapper?: InjectableCss;
    innerWrapper?: InjectableCss;
    wrapper?: InjectableCss;
    gridContainer?: GridContainerProps;
    columnsGridItem?: GridItemProps;
    columnsGridContainer?: GridContainerProps;
    columnGridItem?: GridItemProps;
    columnTitle?: TypographyPresentationProps;
    accordion?: AccordionPresentationProps;
    accordionSection?: AccordionSectionPresentationProps;
    contentGridItem?: GridItemProps;
    contentTitle?: TypographyPresentationProps;
    legalWrapper?: InjectableCss;
    hidden?: HiddenProps;
    separatorGridItem?: GridItemProps;
    legalGridContainer?: GridContainerProps;
    legalLeftGridItem?: GridItemProps;
    legalRightGridItem?: GridItemProps;
    // Store Custom Code
    mobileSeparatorGridItem?: GridItemProps;
    accordionSeparatorGridItem?: GridItemProps;
    columnsGridItemLeft?: GridItemProps;
    columnsGridItemCenter?: GridItemProps;
    columnsGridItemRight?: GridItemProps;
}

export const footerContainerStyles: FooterContainerStyles = {
    widgetWrapper: {
        css: css`
            [href^="mailto:"] {
                word-break: break-word;
            }

            /* Fix CMS visual issues */
            [class^="ZoneStyle-"] {
                height: auto;
            }

            /* Store Custom Code - Start */
            background-color: ${getColor("footer")};

            ${breakpoint("lg")} {
                position: relative;

                &::before,
                &::after {
                    content: "";
                    display: block;
                    position: absolute;
                    top: 0;
                    bottom: 0;
                    width: 50%;
                    z-index: 1;
                }

                &::before {
                    left: 0;
                    background-color: ${getColor("footer.contrast")};
                }

                &::after {
                    right: 0;
                    background-color: ${getColor("footer")};
                }
            }
            /* Store Custom Code - End */
        `,
    },
    innerWrapper: {
        css: siteWidthStyles(),
    },
    wrapper: {
        css: css`
            /* Store Custom Code - Start */
            position: relative;
            z-index: 2;
            color: ${getColor("neutral.contrast")};
            ${breakpoint("md", "down")} {
                padding-top: ${pxToRem(32)};
                padding-bottom: ${pxToRem(48)};
            }

            a {
                color: ${getColor("neutral.contrast")};
            }
            a:hover {
                color: ${getColor("neutral.contrast")};
            }

            ${({ theme }) => css`
                font-size: ${theme.design.typography.size.smaller};
            `}/* Store Custom Code - End */
        `,
    },
    /* Store Custom Code - Start */
    gridContainer: {
        css: css`
            ${breakpoint("lg")} {
                background-repeat: no-repeat;
                background-image: url("data:image/svg+xml,%3C%3Fxml version='1.0'%3F%3E%3Csvg version='1.1' xmlns='http://www.w3.org/2000/svg' x='0px' y='0px' viewBox='83 176 1397 441' xml:space='preserve'%3E%3Cg%3E%3Cpath fill='${getEncodedColor(
                    "footer.contrast",
                )}' d='M83,176v441h653.7C575.2,605,266.8,532.9,83,176z'/%3E%3Cpath fill='${getEncodedColor(
                    "neutral.contrast",
                )}' d='M861.8,617c0,0-6.7,0.1-19-0.4c-93.2-3.9-506.2-44-742.1-440.6H83 c183.8,356.9,492.2,429,653.7,441h105.7H861.8L861.8,617L861.8,617z'/%3E%3Cpath fill='${getEncodedColor(
                    "footer",
                )}' d='M100.7,176c235.9,396.6,648.9,436.6,742.1,440.6c12.3,0.5,638.2,0.4,638.2,0.4V176H100.7z'/%3E%3C/g%3E%3C/svg%3E%0A");
                background-position: 5% top;
                background-size: cover;
            }
            ${breakpoint(1300)} {
                background-size: contain;
            }
        `,
    },
    /* Store Custom Code - End */
    contentTitle: {
        forwardAs: "p",
        css: css`
            ${spacingStyles(null, pxToRem(15))}
            ${({ theme }) => css`
                font-size: ${theme.design.typography.size.larger};
                line-height: ${theme.design.typography.lineHeight.semi};
                font-weight: ${theme.design.typography.weight.bold}; /* Store Custom Code */
            `}
        `,
    },
    /* Store Custom Code - Start */
    columnsGridItemLeft: {
        width: [12, 12, 12, 4, 4],
        css: css`
            ${breakpoint("lg")} {
                padding-bottom: ${pxToRem(50)};
                align-items: flex-end;
            }
        `,
    },
    columnsGridItemCenter: {
        width: [12, 12, 6, 4, 4],
        css: css`
            flex-direction: column;
            padding-top: 0;
            padding-bottom: 0;

            li {
                margin-bottom: 1em;

                ${StyledClickable} {
                    ${footerLinkStyles()}
                }
            }
            ${breakpoint("sm")} {
                padding-top: ${pxToRem(16)};
                padding-bottom: ${pxToRem(16)};
            }
            ${breakpoint("lg")} {
                padding: ${pxToRem(75, 0, 115, 0)};
            }
        `,
    },
    /* Store Custom Code - End */
    separatorGridItem: {
        width: [12, 12, 1, 1, 1], // Store Custom Code
        css: css`
            /* Store Custom Code - Start */
            ${separatorStyles()}
            ${breakpoint("md")} {
                padding-top: ${pxToRem(75)};
                padding-bottom: ${pxToRem(75)};
                &::before {
                    left: 50%;
                    width: ${pxToRem(1)};
                    height: 100%;
                    margin-left: -${pxToRem(1)};
                }
            }
            ${breakpoint("lg")} {
                padding: ${pxToRem(75, 0, 115, 0)};
            }
            /* Store Custom Code - End */
        `,
    },
    /* Store Custom Code - Start */
    mobileSeparatorGridItem: {
        width: [12, 12, 0, 0, 0],
        css: css`
            ${separatorStyles()}
        `,
    },
    accordionSeparatorGridItem: {
        width: [12, 12, 0, 0, 0],
        css: css`
            padding-left: 0;
            padding-right: 0;
            ${separatorStyles()}
        `,
    },
    columnsGridItemRight: {
        width: [12, 12, 5, 3, 3],
        css: css`
            flex-direction: column;

            ${breakpoint("lg")} {
                padding: ${pxToRem(75, 0, 115, 0)};
            }
        `,
    },
    /* Store Custom Code - End */
    columnGridItem: {
        width: 6, // Store Custom Code
        css: css`
            flex-direction: column;
        `,
    },
    columnTitle: {
        forwardAs: "p",
        css: css`
            ${({ theme }) => css`
                ${spacingStyles(null, pxToRem(19))}
                font-size: ${theme.design.typography.size.base};
                font-weight: ${theme.design.typography.weight.bold};
            `}
        `,
    },
    hidden: {
        css: css`
            width: 100%;
        `,
    },
    legalWrapper: {
        css: css`
            /* Store Custom Code - Start */
            position: relative;
            z-index: 2;
            ${breakpointValues([pxToRem(20, 0), null, pxToRem(25, 0)], "padding")}
            /* Store Custom Code - End */
            background-color: ${getColor("footer.alt")};
            color: ${getColor("footer.altContrast")};
            font-size: ${x => x.theme.design.typography.size.smallest};
            /* Store Custom Code - Start */
            ${breakpoint("sm", "down")} {
                p {
                    padding-top: ${pxToRem(16)};
                    padding-bottom: ${pxToRem(16)};
                }
                ul {
                    padding-bottom: ${pxToRem(16)};
                }
            }
            /* Store Custom Code - End */
        `,
    },
    legalGridContainer: {
        gap: 0,
        css: css`
            ${gridGapStyles(pxToRemArray([30]), pxToRemArray([6]))}

            li ${StyledClickable} {
                ${footerLinkStyles() /* Store Custom Code */}
            }
        `,
    },
    legalLeftGridItem: {
        width: [12, 12, 6, 6, 6],
        css: legalAlignStyles("left"),
    },
    legalRightGridItem: {
        width: [12, 12, 6, 6, 6],
        css: css`
            ${legalAlignStyles("right")}
            /* Store Custom Code - Start */
            ${breakpoint("sm", "down")} {
                li {
                    display: block;
                    padding-top: ${pxToRem(8)};
                    padding-bottom: ${pxToRem(8)};
                }
            }
            /* Store Custom Code - End */
        `,
    },
    /* Store Custom Code - Start */
    logoWrapper: {
        css: css`
            img {
                width: ${pxToRem(146)};
                height: ${pxToRem(54)};
                ${breakpoint("lg")} {
                    width: ${pxToRem(206)};
                    height: ${pxToRem(77)};
                }
            }

            ${StyledImgWrapper} {
                background: transparent !important;
            }
        `,
    },
    /* Store Custom Code - End */
    accordionSection: accordionFooterSectionProps,
};

const FooterContainer: FC<OwnProps> = ({
    id,
    extendedStyles,
    // Valtech Custom Code
    fields: { contentTitle, firstColumnTitle, secondColumnTitle, thirdColumnTitle },
}) => {
    const [styles] = React.useState(() => mergeToNew(footerContainerStyles, extendedStyles));

    /* Valtech Custom Code */
    return (
        <StyledWrapper {...styles.widgetWrapper}>
            <StyledWrapper {...styles.wrapper}>
                <StyledWrapper {...styles.innerWrapper}>
                    <GridContainer {...styles.gridContainer}>
                        {/* Store Custom Code - Start */}
                        <GridItem {...styles.columnsGridItemLeft}>
                            <StyledWrapper {...styles.logoWrapper}>
                                <Zone zoneName="Logo" contentId={id} />
                            </StyledWrapper>
                        </GridItem>
                        {/* Store Custom Code - End */}
                        {/* Store Moved - <Zone zoneName="Content" contentId={id} /> - down */}

                        <GridItem {...styles.mobileSeparatorGridItem /* Store Custom Code */}></GridItem>
                        <GridItem {...styles.columnsGridItemCenter} /* Store Custom Code */>
                            <Hidden above="xs" {...styles.hidden}>
                                <Accordion headingLevel={6} {...styles.accordion}>
                                    <ManagedAccordionSection
                                        title={firstColumnTitle}
                                        uid="footer-accordion-1"
                                        {...styles.accordionSection}
                                    >
                                        <Zone zoneName="FirstColumn" contentId={id} />
                                    </ManagedAccordionSection>
                                    <ManagedAccordionSection
                                        title={secondColumnTitle}
                                        uid="footer-accordion-2"
                                        {...styles.accordionSection}
                                    >
                                        <Zone zoneName="SecondColumn" contentId={id} />
                                    </ManagedAccordionSection>
                                    {/* Store Removed - ThirdColumn */}
                                </Accordion>
                            </Hidden>
                            <Hidden below="sm" {...styles.hidden}>
                                <GridContainer {...styles.columnsGridContainer}>
                                    <GridItem {...styles.columnGridItem}>
                                        <Typography {...styles.columnTitle}>{firstColumnTitle}</Typography>
                                        <Zone zoneName="FirstColumn" contentId={id} />
                                    </GridItem>
                                    <GridItem {...styles.mobileSeparatorGridItem /* Store Custom Code */}></GridItem>
                                    <GridItem {...styles.columnGridItem}>
                                        <Typography {...styles.columnTitle}>{secondColumnTitle}</Typography>
                                        <Zone zoneName="SecondColumn" contentId={id} />
                                    </GridItem>
                                    {/* Store Removed - ThirdColumn */}
                                </GridContainer>
                            </Hidden>
                        </GridItem>
                        {/* Store Custom Code - Start */}
                        <GridItem {...styles.separatorGridItem}></GridItem>
                        <GridItem {...styles.columnsGridItemRight}>
                            {contentTitle ? <Typography {...styles.contentTitle}>{contentTitle}</Typography> : null}
                            <Zone zoneName="Content" contentId={id} />
                        </GridItem>
                        {/* Store Custom Code - End */}
                    </GridContainer>
                </StyledWrapper>
            </StyledWrapper>
            <StyledWrapper {...styles.legalWrapper}>
                <StyledWrapper {...styles.innerWrapper}>
                    <GridContainer {...styles.legalGridContainer}>
                        <GridItem {...styles.legalLeftGridItem}>
                            <Zone zoneName="LegalLeft" contentId={id} />
                        </GridItem>
                        <GridItem {...styles.legalRightGridItem}>
                            <Zone zoneName="LegalRight" contentId={id} />
                        </GridItem>
                    </GridContainer>
                </StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
    /* Valtech Custom Code */
};

const footerContainer: WidgetModule = {
    component: FooterContainer,
    definition: {
        group: "Footer",
        allowedContexts: ["Footer"],
        /* Valtech Custom Code */
        fieldDefinitions: [
            {
                name: fields.contentTitle,
                displayName: "Content title",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
            },
            {
                name: fields.firstColumnTitle,
                displayName: "First Title",
                editorTemplate: "TextField",
                defaultValue: "",
                isRequired: true,
                fieldType: "Translatable",
            },
            {
                name: fields.secondColumnTitle,
                displayName: "Second Title",
                editorTemplate: "TextField",
                defaultValue: "",
                isRequired: true,
                fieldType: "Translatable",
            },
            /* Store Removed - fields.thirdColumnTitle */
        ],
        /* Valtech Custom Code */
    },
};

export default footerContainer;
